import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, AlertTriangle, Loader2, Link2Off, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '@/lib/supabase';
import { syncAdsJetzt } from '@/lib/analytics-api';

/**
 * Gesundheitsstatus der Conversion-Kette (LMS → Google Ads).
 *
 * Warum es das gibt: Bis zum 24.08.2026 hakte der Nacht-Lauf jeden Lead als
 * „hochgeladen" ab — auch den, den Google abgelehnt hatte. Bei Schreinerei
 * Thomas kam wochenlang keine einzige Conversion im Konto an, ohne dass es
 * irgendwo sichtbar war. Diese Kachel beantwortet die Frage jetzt direkt im
 * Dashboard: Kommen unsere Conversions an — ja oder nein?
 *
 * Bewusst auch im GUTEN Fall sichtbar. Eine Anzeige, die nur bei Fehlern
 * erscheint, lässt offen, ob gerade alles läuft oder nur niemand hinsieht.
 */
export default function ConversionKette({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['conversion-kette', projectId],
    queryFn: async () => {
      const seit = new Date(Date.now() - 30 * 86400000).toISOString();
      const [logs, offen, gescheitert, projekt] = await Promise.all([
        // NUR echte Uploads. Im selben Protokoll stehen auch die Nachtwächter
        // (zielvorhaben, conversion_struktur) — deren Fehlschlag ist keine
        // Aussage darüber, ob Conversions ankommen. Bis 18.09.2026 färbte ein
        // abgelehnter Struktur-Umbau die Kachel rot („Übertragung kam nicht an"),
        // obwohl alle Uploads von Holzzauber und Fliesen Schwarze durchliefen.
        supabase
          .from('ads_upload_log')
          .select('art, versucht, erfolgreich, fehlgeschlagen, fehler, created_at')
          .eq('project_id', projectId)
          .in('art', ['anfrage', 'kontakt_klick', 'auftrag'])
          .gte('created_at', seit)
          .order('created_at', { ascending: false })
          .limit(60),
        supabase
          .from('leads')
          .select('id', { count: 'exact', head: true })
          .eq('project_id', projectId)
          .not('gclid', 'is', null)
          .is('ads_uploaded_at', null)
          .is('ads_upload_failed_at', null)
          // Spam wird nie übertragen — sonst „wartet" er hier für immer.
          .or('outcome.is.null,outcome.neq.spam'),
        supabase
          .from('leads')
          .select('id', { count: 'exact', head: true })
          .eq('project_id', projectId)
          .not('ads_upload_failed_at', 'is', null),
        supabase
          .from('projects')
          .select('conversion_struktur, conversion_struktur_geprueft')
          .eq('id', projectId)
          .maybeSingle(),
      ]);
      const rows = logs.data || [];
      // Maßgeblich für „läuft es?" ist der ZULETZT gelaufene Versuch je Art,
      // nicht die Summe über 30 Tage: Ein behobener Fehler von heute früh
      // darf die Kachel nicht dauerhaft rot färben. Die Erfolgszahl bleibt
      // die Summe — sie beantwortet die andere Frage, nämlich wie viel
      // insgesamt angekommen ist.
      const letzteJeArt = new Map<string, any>();
      for (const r of rows) if (!letzteJeArt.has(r.art)) letzteJeArt.set(r.art, r);
      const aktuell = [...letzteJeArt.values()];

      return {
        erfolgreich: rows.reduce((s, r) => s + (r.erfolgreich || 0), 0),
        fehlgeschlagen: aktuell.reduce((s, r) => s + (r.fehlgeschlagen || 0), 0),
        letzterFehler: aktuell.find((r) => r.fehler)?.fehler || null,
        letzterLauf: rows[0]?.created_at || null,
        // Nur zur Einordnung: Wie viele Fehlversuche gab es in 30 Tagen
        // insgesamt? Erscheint als Nebensatz, wenn gerade alles läuft.
        fehlerHistorisch: rows.reduce((s, r) => s + (r.fehlgeschlagen || 0), 0),
        wartend: offen.count || 0,
        aufgegeben: gescheitert.count || 0,
        struktur: (projekt.data?.conversion_struktur as any) || null,
        strukturGeprueft: projekt.data?.conversion_struktur_geprueft || null,
      };
    },
    staleTime: 60_000,
  });

  // Übertragung sofort auslösen, statt bis zum Nacht-Lauf zu warten. Der
  // Knopf gehört genau hierhin: Wer liest, dass Anfragen auf die Übertragung
  // warten, will sie jetzt schicken — nicht erst die Kampagnen-Tabelle
  // weiter unten suchen.
  const uebertragen = useMutation({
    mutationFn: () => syncAdsJetzt(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['conversion-kette', projectId] });
      await refetch();
      toast.success('Übertragung ausgeführt — Ergebnis steht oben');
    },
    onError: (e: any) => toast.error(e?.message || 'Übertragung fehlgeschlagen'),
  });

  const UebertragenKnopf = ({ hell }: { hell?: boolean }) => (
    <button
      onClick={() => uebertragen.mutate()}
      disabled={uebertragen.isPending}
      title="Sendet alle offenen Conversions sofort an Google Ads und liest zurück, was angekommen ist. Läuft sonst nur nachts."
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px] font-medium disabled:opacity-60 ${
        hell ? 'border-gray-300 text-gray-600 hover:bg-gray-50' : 'border-current/30 hover:bg-white/40'
      }`}
    >
      {uebertragen.isPending ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <RefreshCw className="h-3 w-3" />
      )}
      {uebertragen.isPending ? 'Überträgt …' : 'Jetzt übertragen & prüfen'}
    </button>
  );

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center gap-2 text-sm text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Conversion-Kette wird geprüft …
      </div>
    );
  }
  if (!data) return null;

  const { erfolgreich, fehlgeschlagen, letzterFehler, letzterLauf, wartend, aufgegeben, fehlerHistorisch, struktur } = data;
  const nieGelaufen = !letzterLauf && erfolgreich === 0 && fehlgeschlagen === 0;
  const kaputt = fehlgeschlagen > 0 || aufgegeben > 0;

  // Noch kein Lauf seit dem Umbau: nichts behaupten, was nicht geprüft ist.
  if (nieGelaufen) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-500">
        <span className="flex items-center gap-2 min-w-0">
          <Link2Off className="w-4 h-4 text-gray-400 shrink-0" />
          <span>
            Conversion-Kette: noch kein protokollierter Upload
            {wartend > 0 ? ` — ${wartend} Anfrage(n) warten auf die Übertragung` : ''}.
          </span>
        </span>
        <UebertragenKnopf hell />
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border px-4 py-3 ${
        kaputt ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
      }`}
    >
      <div className="flex items-start gap-2">
        {kaputt ? (
          <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
        ) : (
          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
          <p className={`text-sm font-medium ${kaputt ? 'text-red-800' : 'text-green-800'}`}>
            {kaputt
              ? `Conversion-Kette gestört: ${fehlgeschlagen} Übertragung(en) kamen nicht im Ads-Konto an`
              : `Conversion-Kette in Ordnung — ${erfolgreich} Conversion(s) in 30 Tagen im Ads-Konto angekommen`}
          </p>
          <span className={kaputt ? 'text-red-700' : 'text-green-700'}><UebertragenKnopf /></span>
          </div>
          <p className={`text-[11px] mt-0.5 ${kaputt ? 'text-red-600' : 'text-green-700'}`}>
            {letzterLauf && `zuletzt geprüft ${new Date(letzterLauf).toLocaleString('de-DE')}`}
            {wartend > 0 && ` · ${wartend} wartet auf die nächste Übertragung`}
            {aufgegeben > 0 && ` · ${aufgegeben} endgültig fehlgeschlagen (nach 3 Versuchen)`}
            {!kaputt && fehlerHistorisch > 0 && ` · ${fehlerHistorisch} frühere Fehlversuche, inzwischen behoben`}
          </p>
          {kaputt && letzterFehler && (
            <p className="text-[11px] text-red-700 mt-1 font-mono break-words">{letzterFehler}</p>
          )}
          {kaputt && (
            <p className="text-[11px] text-red-600 mt-1">
              Solange das besteht, optimiert Google ohne unsere Anfragen — die Gebotsautomatik arbeitet blind.
            </p>
          )}
          {/* Doppelzählung ist ein eigenes Problem: Die Übertragung kann
              tadellos laufen und trotzdem zählt jeder Kontakt zweimal, weil
              im Konto noch altes Browser-Tracking primär mitläuft. */}
          {struktur?.doppelzaehlung?.length > 0 && (
            <p className="text-[11px] text-amber-700 mt-1.5 border-t border-current/10 pt-1.5">
              <strong>Doppelzählung im Konto:</strong> {struktur.doppelzaehlung.join(', ')} zählt dieselben
              Kontakte noch einmal.{' '}
              {struktur.handlungsbedarf
                ? 'Die Empfehlungen enthalten dafür einen Umstellungs-Vorschlag zum Anklicken.'
                : 'Umgestellt wird erst, wenn unsere eigenen Conversions im Konto ankommen — sonst verliert die Gebotsautomatik jedes Ziel.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
